import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListZones } from 'src/features/zone/api/useListZones';
import CreateZoneFormModal from 'src/features/zone/components/form/CreateZoneFormModal';
import CellZoneActions from 'src/features/zone/components/table/CellZoneActions';
import { Zone } from 'src/features/zone/utils/types';
import { zoneTypeBadgeOptions } from 'src/features/zone/utils/constants';
import { zoneMessages } from 'src/features/zone/utils/messages';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import usePagination from 'src/registry/hooks/usePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<Zone>();

function Zones() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const manageZones = useHasPermissions([PERMISSION.MANAGE_ZONES]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [parkingId, queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions],
    );

    const { data, isLoading, isError } = useListZones(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(zoneMessages.zones));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) => row.original.translations.find((translation) => translation.language === locale)?.name,
            meta: {
                title: formatMessage(zoneMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(zoneMessages.type),
                badgeOptions: zoneTypeBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('site', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) =>
                row.original.site.translations.find((translation) => translation.language === locale)?.name,
            meta: {
                title: formatMessage(zoneMessages.site),
            },
        }),
        columnHelper.accessor('parent_zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) =>
                row.original.parent_zone
                    ? row.original.parent_zone.translations.find((translation) => translation.language === locale)?.name
                    : '-',
            meta: {
                title: formatMessage(zoneMessages.parentZone),
            },
            id: 'parent_zone_name',
        }),
        columnHelper.accessor('spaces_count', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(zoneMessages.spacesCount),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellZoneActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Zone, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(zoneMessages.zones),
                entryName: formatMessage(zoneMessages.zone).toLowerCase(),
                entryNames: formatMessage(zoneMessages.zones).toLowerCase(),
                placeholder: formatMessage(zoneMessages.zone),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageZones && { newEntry: CreateZoneFormModal }),
            }}
        />
    );
}

export default Zones;
